import clsx from 'clsx';
import { useState, Activity, useEffect } from 'react';

import {
  IconBan,
  IconEye,
  IconCheck,
  IconSearch,
  IconClockHour3,
  IconAdjustments,
  IconCircleCheck,
  IconInfoTriangle,
  IconDeviceGamepad2,
} from '@tabler/icons-react';

import ActionDropdown from '@/components/ActionDropdown';
import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import ExportDropdown from '@/components/ExportDropdown';
import Input from '@/components/Input/Input';
import { SummaryCard } from '@/components/SummaryCard';
import CustomTable from '@/components/Table';
import { LessonStageConfig, ContentStatusConfig } from '@/constants/constant';
import { EExportType } from '@/enum';
import { useDebounce } from '@/hooks/useDebounce';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getContentList,
  getContentSummary,
  exportContentList,
  updateContentStatus,
} from '@/service/contentService';
import {
  IContentGameData,
  TContentSummaryItem,
  IContentSummaryData,
} from '@/types';
import {
  downloadFile,
  formatLastActive,
  getStartOfMonthUnix,
  formatSecondsToDuration,
} from '@/utils';

import FilterModal from './modals/FilterModal';
import GameDetailsModal from './modals/GameDetailsModal';

const CONTENT_SUMMARY_CONFIG = [
  {
    key: 'total_games_published',
    icon: <IconDeviceGamepad2 color="#717784" />,
    label: 'contentManagement.summary-cards.total-games',
    value: '56',
    trend: '8%',
    trendDirection: 'UP',
    isPositive: true,
  },
  {
    key: 'active_games',
    icon: <IconCheck color="#717784" />,
    label: 'contentManagement.summary-cards.active-games',
    value: '52',
    trend: '12%',
    trendDirection: 'UP',
    isPositive: true,
  },
  {
    key: 'avg_engagement_time',
    icon: <IconClockHour3 color="#717784" />,
    label: 'contentManagement.summary-cards.avg-engagement',
    value: '84%',
    trend: '5%',
    trendDirection: 'UP',
    isPositive: true,
  },
  {
    key: 'disabled_items',
    icon: <IconInfoTriangle color="#717784" />,
    label: 'contentManagement.summary-cards.disabled-items',
    value: '11',
    trend: '2%',
    trendDirection: 'UP',
    isPositive: false,
  },
];

const ContentManagement = () => {
  const { setTitle, setIsBreadcrumb } = usePageTitle();
  const { formatText } = useFormatText();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search?.trim(), 500);

  const [games, setGames] = useState<IContentGameData[]>([]);
  const [total, setTotal] = useState(0);
  const [isTableLoading, setIsTableLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('DESC');

  const [filters, setFilters] = useState({
    status: '',
    stage: '',
    quarter: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({
    status: '',
    stage: '',
    quarter: '',
  });

  const [selectedGameForView, setSelectedGameForView] =
    useState<IContentGameData | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [selectedGameForRestrict, setSelectedGameForRestrict] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isRestrictModalOpen, setIsRestrictModalOpen] = useState(false);

  const [summaryData, setSummaryData] = useState<IContentSummaryData | null>(
    null
  );
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [isRestrictLoading, setIsRestrictLoading] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);

  const fetchSummary = async () => {
    setIsSummaryLoading(true);
    try {
      const monthTimestamp = getStartOfMonthUnix();

      const res = await getContentSummary(monthTimestamp);
      if (res?.status && res?.data) {
        setSummaryData(res?.data);
      }
    } catch (error) {
      console.error('Error fetching content summary:', error);
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const fetchGames = async () => {
    setIsTableLoading(true);
    try {
      const query: Record<string, unknown> = {
        offset: (currentPage - 1) * pageSize,
        limit: pageSize,
        search: debouncedSearch || undefined,
        status: appliedFilters?.status,
        category: appliedFilters?.stage,
        quarter: appliedFilters?.quarter || undefined,
      };

      if (sortColumn && sortDirection) {
        query[`order[${sortColumn}]`] = sortDirection;
      }

      const res = await getContentList(query);
      if (res?.status) {
        setGames(res?.data || []);
        setTotal(res?.total || 0);
      }
    } catch (error) {
      console.error('Error fetching game list:', error);
    } finally {
      setIsTableLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    currentPage,
    pageSize,
    sortColumn,
    sortDirection,
    appliedFilters,
  ]);

  const statsData: TContentSummaryItem[] = CONTENT_SUMMARY_CONFIG.map(
    (item) => {
      const apiItem = summaryData?.[item.key as keyof IContentSummaryData];
      const apiValue =
        item.key === 'avg_engagement_time'
          ? formatSecondsToDuration(apiItem?.total)
          : String(apiItem?.total);
      return {
        ...item,
        value: apiItem ? apiValue : item?.value,
        trend: apiItem ? `${apiItem?.growth}%` : item?.trend,
        trendDirection: apiItem ? apiItem?.trend : item?.trendDirection,
        isPositive: apiItem ? apiItem?.trend !== 'DOWN' : item?.isPositive,
        trendLabel: 'common.this-month',
      };
    }
  );

  const pageTitle = formatText('contentManagement.title');

  useEffect(() => {
    document.title = pageTitle;
    setTitle(pageTitle);
    setIsBreadcrumb([{ label: pageTitle }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTitle]);

  const handleSort = (key: string) => {
    if (sortColumn === key) {
      setSortDirection(
        sortDirection === 'ASC'
          ? 'DESC'
          : sortDirection === 'DESC'
            ? 'ASC'
            : 'ASC'
      );
    } else {
      setSortColumn(key);
      setSortDirection('ASC');
    }
    setCurrentPage(1);
  };

  const handleUpdateGameStatus = async (id: string, status: boolean) => {
    setIsRestrictLoading(true);
    try {
      const res = await updateContentStatus(id, status);
      if (res?.status) {
        if (status) setIsRestrictModalOpen(false);
        fetchGames();
        fetchSummary();
      }
    } catch (error) {
      console.error('Error updating game status:', error);
    } finally {
      setIsRestrictLoading(false);
    }
  };

  const handleExport = async (type: 'csv' | 'pdf') => {
    setIsExportLoading(true);
    try {
      const query: Record<string, unknown> = {
        search: debouncedSearch || undefined,
        status: appliedFilters?.status,
        category: appliedFilters?.stage,
        quarter: appliedFilters?.quarter || undefined,
        type: type === 'csv' ? EExportType.CSV : EExportType.PDF,
      };

      const res = await exportContentList(query);
      if (res) {
        const label = pageTitle.replace(/\s+/g, '_').toLowerCase();
        downloadFile(res, `${label}_${new Date().getTime()}.${type}`);
      }
    } catch (error) {
      console.error('Error exporting content:', error);
    } finally {
      setIsExportLoading(false);
    }
  };

  const columns = [
    {
      title: 'contentManagement.table.columns.games',
      key: 'title',
      width: '300px',
      sorter: true,
      handleSort: () => handleSort('title'),
      align: 'left',
      render: (row: IContentGameData) =>
        row?.title ? (
          <div className="flex items-center gap-2">
            <AvatarGroupList
              list={[
                {
                  id: row?.id,
                  name: row?.title,
                  image: row?.preview_image ? `${row?.preview_image}` : '',
                },
              ]}
              size={40}
              avatarClassName="w-8 h-8 text-[10px]"
            />
            <span className="text-sm font-medium text-neutral-primary">
              {row?.title}
            </span>
          </div>
        ) : (
          '-'
        ),
    },
    {
      title: 'contentManagement.table.columns.quarter',
      key: 'quarters',
      width: '100px',
      sorter: true,
      handleSort: () => handleSort('quarters'),
      render: (row: IContentGameData) => (
        <span className="text-sm font-medium text-neutral-primary">
          {row?.quarters ?? '-'}
        </span>
      ),
    },
    {
      title: 'contentManagement.table.columns.round',
      key: 'rounds',
      width: '100px',
      sorter: true,
      handleSort: () => handleSort('rounds'),
      render: (row: IContentGameData) => (
        <span className="text-sm font-medium text-neutral-primary">
          {row?.rounds ?? '-'}
        </span>
      ),
    },
    {
      title: 'contentManagement.table.columns.stage',
      key: 'category',
      width: '200px',
      sorter: true,
      handleSort: () => handleSort('category'),
      render: (row: IContentGameData) => {
        const config =
          LessonStageConfig[row?.category as keyof typeof LessonStageConfig];
        return config ? (
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap',
              config?.className
            )}
          >
            {formatText(config?.label)}
          </span>
        ) : (
          '-'
        );
      },
    },

    {
      title: 'contentManagement.table.columns.play-count',
      key: 'play_count',
      width: '150px',
      sorter: true,
      handleSort: () => handleSort('play_count'),
      render: (row: IContentGameData) => (
        <span className="bg-neutral-light-100 rounded-full px-2 py-1 text-xs font-semibold">
          {row.play_count?.toLocaleString() ?? 0}
        </span>
      ),
    },
    {
      title: 'contentManagement.table.columns.status',
      key: 'status',
      width: '150px',
      sorter: true,
      handleSort: () => handleSort('status'),
      render: (row: IContentGameData) => {
        const config =
          ContentStatusConfig[row.status as keyof typeof ContentStatusConfig];
        return (
          <span
            className={clsx(
              'px-2 py-1 rounded-full text-xs font-semibold',
              config?.className
            )}
          >
            {formatText(
              config?.label || 'contentManagement.table.status.active'
            )}
          </span>
        );
      },
    },
    {
      title: 'contentManagement.table.columns.last-update',
      key: 'last_update',
      width: '150px',
      sorter: true,
      handleSort: () => handleSort('last_update'),
      render: (row: IContentGameData) =>
        formatLastActive(row?.last_update ?? undefined, formatText),
    },
    {
      title: 'contentManagement.table.columns.action',
      key: 'action',
      width: '100px',
      align: 'center',
      render: (row: IContentGameData) => {
        const actions = [
          {
            key: 'view',
            label: 'contentManagement.table.actions.view',
            icon: <IconEye size={18} />,
            onClick: () => {
              setSelectedGameForView(row);
              setIsViewModalOpen(true);
            },
          },
          row.status === 'active'
            ? {
                key: 'restrict',
                label: 'contentManagement.table.actions.restrict',
                icon: <IconBan size={18} />,
                isDanger: true,
                onClick: () => {
                  setSelectedGameForRestrict({ id: row?.id, name: row?.title });
                  setIsRestrictModalOpen(true);
                },
              }
            : {
                key: 'unrestrict',
                label: 'contentManagement.table.actions.unrestrict',
                icon: <IconCircleCheck size={18} />,
                onClick: () => handleUpdateGameStatus(row?.id, false),
              },
        ];

        return <ActionDropdown actions={actions} />;
      },
    },
  ];

  const isFilterApplied =
    !!appliedFilters?.status ||
    !!appliedFilters?.stage ||
    !!appliedFilters?.quarter;

  return (
    <>
      <div className="flex flex-wrap justify-between items-center gap-2.5 mb-6 ps-1">
        <div className="flex flex-wrap items-center gap-2.5">
          <Input
            name="search"
            placeholder={formatText('contentManagement.search-placeholder')}
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
            leftIcon={{
              icon: <IconSearch size={20} className="text-neutral-500" />,
            }}
            className="max-w-77 md:min-w-77 w-full"
            inputClassName="py-[13px]"
          />
          <div className="relative">
            <Button
              icon={<IconAdjustments size={20} />}
              label={formatText('common.actions.filter')}
              variant="outline"
              className={clsx(
                'px-4 h-12 cursor-pointer',
                isFilterApplied && 'border-brand-default! text-brand-default!'
              )}
              onClick={() => setIsFilterModalOpen(true)}
            />
            {isFilterApplied && (
              <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-brand-default border-2 border-white" />
            )}
          </div>
        </div>
        <ExportDropdown
          onCsvExport={() => handleExport('csv')}
          onPdfExport={() => handleExport('pdf')}
          disabled={games?.length === 0 || isExportLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
        {statsData?.map((stat, index) => (
          <SummaryCard
            key={index}
            variant="dashboard"
            label={formatText(stat.label)}
            value={stat?.value}
            icon={stat?.icon}
            trendDirection={stat?.trendDirection}
            isPositive={stat?.isPositive}
            trendLabel={
              stat?.trendLabel ? formatText(stat?.trendLabel) : undefined
            }
            trend={stat?.trend}
            isLoading={isSummaryLoading}
          />
        ))}
      </div>

      <div className="mt-6">
        <CustomTable
          isLoading={isTableLoading}
          dataSource={games}
          columns={columns}
          total={total}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          fieldName={sortColumn}
          sortDirection={sortDirection}
          isEmpty={games?.length === 0 && !isTableLoading && !isFilterApplied}
          emptyTitle={formatText('contentManagement.not-found-title')}
          emptyDescription={formatText('contentManagement.not-found-desc')}
          isEmptySearch={
            games?.length === 0 &&
            (search?.length > 0 ||
              debouncedSearch?.length > 0 ||
              isFilterApplied)
          }
          emptyImgSrc={'/assets/svg/empty-content.svg'}
          onRowClick={(id) => {
            const row = games.find((item) => item.id === id);
            if (row) {
              setSelectedGameForView(row);
              setIsViewModalOpen(true);
            }
          }}
        />
      </div>

      <Activity mode={isViewModalOpen ? 'visible' : 'hidden'}>
        <GameDetailsModal
          game={selectedGameForView}
          setIsOpen={setIsViewModalOpen}
          onSuccess={fetchGames}
        />
      </Activity>

      <Activity mode={isFilterModalOpen ? 'visible' : 'hidden'}>
        <FilterModal
          setIsOpen={setIsFilterModalOpen}
          filters={filters}
          setFilters={setFilters}
          onApply={(f) => setAppliedFilters(f)}
        />
      </Activity>

      <Activity mode={isRestrictModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isRestrictModalOpen}
          setIsOpen={setIsRestrictModalOpen}
          title="contentManagement.table.actions.restrict-title"
          descriptionPrefix="contentManagement.table.actions.restrict-prefix"
          entityName={selectedGameForRestrict?.name}
          entityFallback="contentManagement.table.actions.restrict-title"
          confirmLabel={formatText(
            'contentManagement.table.actions.restrict-confirm'
          )}
          onConfirm={() =>
            handleUpdateGameStatus(selectedGameForRestrict?.id ?? '', true)
          }
          isLoading={isRestrictLoading}
          isDestructive
        />
      </Activity>
    </>
  );
};

export default ContentManagement;
